import { X } from 'lucide-react';
import type { WhatsappTemplateSummary } from '../../api/types';
import {
  headerNeedsMedia,
  headerTextParams,
  missingFields,
  renderPreview,
  variableLabel,
  type WhatsappTemplateDraft,
} from '../../lib/whatsappTemplates';

/**
 * The variable form for a picked template. It sits above the composer rather than in
 * a modal because the preview has to stay next to the thread it is being sent into —
 * an approved template body is fixed text and the agent's only real check is reading
 * it back with the values in place.
 */
export function WhatsappTemplatePanel({
  template,
  draft,
  onChange,
  onCancel,
}: {
  template: WhatsappTemplateSummary;
  draft: WhatsappTemplateDraft;
  onChange: (next: WhatsappTemplateDraft) => void;
  onCancel: () => void;
}) {
  const missing = missingFields(template, draft);
  const setAt = (key: 'body' | 'headerText' | 'buttons', index: number, value: string) => {
    const next = [...draft[key]];
    next[index] = value;
    onChange({ ...draft, [key]: next });
  };

  return (
    <div className="cmp-tpl">
      <div className="cmp-tpl-head">
        <span className="cmp-tpl-name">
          {template.name} · {template.language}
        </span>
        <button type="button" className="cmp-tpl-close" onClick={onCancel} title="Clear template">
          <X size={13} />
        </button>
      </div>

      {headerNeedsMedia(template) && (
        <label className="cmp-tpl-field">
          <span>Header {template.header?.format.toLowerCase()} URL</span>
          <input
            className="input"
            value={draft.headerMediaUrl}
            placeholder="https://…"
            onChange={(e) => onChange({ ...draft, headerMediaUrl: e.target.value })}
          />
          {/* Meta fetches the file itself, so a signed url the agent cannot share
              publicly will fail on Meta's side, not ours. */}
          <span className="field-hint">Must be reachable by Meta without authentication.</span>
        </label>
      )}

      {headerTextParams(template).map((name, index) => (
        <label className="cmp-tpl-field" key={`h-${name}`}>
          <span>Header {variableLabel(name)}</span>
          <input
            className="input"
            value={draft.headerText[index] ?? ''}
            onChange={(e) => setAt('headerText', index, e.target.value)}
          />
        </label>
      ))}

      {template.bodyParams.map((name, index) => (
        <label className="cmp-tpl-field" key={`b-${name}`}>
          <span>{variableLabel(name)}</span>
          <input
            className="input"
            value={draft.body[index] ?? ''}
            onChange={(e) => setAt('body', index, e.target.value)}
          />
        </label>
      ))}

      {template.buttons.map((button, index) =>
        button.expectsParam ? (
          <label className="cmp-tpl-field" key={`btn-${button.index}`}>
            <span>Button “{button.text || button.type}”</span>
            <input
              className="input"
              value={draft.buttons[index] ?? ''}
              onChange={(e) => setAt('buttons', index, e.target.value)}
            />
          </label>
        ) : null,
      )}

      <div className="cmp-tpl-preview">{renderPreview(template, draft)}</div>
      {template.footerText && <div className="cmp-tpl-footer">{template.footerText}</div>}
      {missing.length > 0 && (
        <div className="cmp-tpl-missing">Still needed: {missing.join(', ')}</div>
      )}
    </div>
  );
}
